/**
 * Tip Modal - Feature H
 * Quick tip entry after payment
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DollarSign, Heart } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function TipModal({ isOpen, onClose, orderId, orderTotal }) {
  const [selectedTip, setSelectedTip] = useState(null);
  const [customTip, setCustomTip] = useState('');

  const quickTips = [
    { label: '5%', amount: orderTotal * 0.05 },
    { label: '10%', amount: orderTotal * 0.10 },
    { label: '15%', amount: orderTotal * 0.15 },
    { label: '20%', amount: orderTotal * 0.20 }
  ];

  const handleAddTip = async (amount) => {
    if (!amount || amount <= 0) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/billing/${orderId}/add-tip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tip_amount: amount,
          tip_method: 'card'
        })
      });

      if (response.ok) {
        toast.success('Trinkgeld hinzugefügt!', {
          description: `${amount.toFixed(2)}€ - Vielen Dank! 🙏`,
          icon: '💚'
        });
        onClose();
      }
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px] bg-[#0E1628] border-slate-700/50">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
        
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Heart className="w-6 h-6 text-emerald-400 fill-emerald-400" />
            Trinkgeld?
          </DialogTitle>
          <p className="text-sm text-slate-400 mt-2">
            Bestellung: {orderTotal.toFixed(2)}€
          </p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Quick Tip Buttons */}
          <div className="grid grid-cols-2 gap-3">
            {quickTips.map((tip) => (
              <Button
                key={tip.label}
                variant="outline"
                onClick={() => handleAddTip(tip.amount)}
                className={`h-16 flex flex-col items-center justify-center gap-1 border-2
                          ${selectedTip === tip.label ? 'border-emerald-500 bg-emerald-500/15' : 'border-slate-700 hover:border-emerald-400/50'}`}
              >
                <span className="text-lg font-bold text-white">{tip.label}</span>
                <span className="text-sm text-emerald-400" style={{ fontFamily: "'Fira Code', monospace" }}>
                  {tip.amount.toFixed(2)}€
                </span>
              </Button>
            ))}
          </div>

          {/* Custom Amount */}
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="number"
              step="0.50"
              value={customTip}
              onChange={(e) => setCustomTip(e.target.value)}
              placeholder="Eigener Betrag..."
              className="w-full pl-11 pr-4 py-3 bg-[#111C32] border-2 border-slate-700 rounded-lg text-white text-lg
                       focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 outline-none"
              style={{ fontFamily: "'Fira Code', monospace" }}
            />
          </div>
          
          {customTip && (
            <Button
              onClick={() => handleAddTip(parseFloat(customTip))}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white h-12"
            >
              {parseFloat(customTip).toFixed(2)}€ Trinkgeld hinzufügen
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            Kein Trinkgeld
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
